// registry.js — registro dei moduli della shell (§3, contratto v2 superset del v1).
// Puro (zero DOM), testato con node --test (server/src/lib/shell-registry.test.js, B2.9).
//
// Regole B2.9:
//  - register() valida: id obbligatorio ([a-z][a-z0-9-]*); campi con tipo errato =
//    THROW con l'ELENCO dei campi sbagliati (sempre, anche in prod: spec invalida = bug).
//  - seconda register con lo STESSO id e version >= esistente = UPGRADE (merge, i campi
//    nuovi vincono, mai duplicato) — è il meccanismo degli adapter R4.
//  - register con version INFERIORE a quella registrata (v1 sopra v2) o due v2 sullo
//    stesso id = COLLISIONE tra proprietari diversi: throw in dev/staging,
//    console.error + spec esistente preservata in prod.

import { defaultIsDev } from './bus.js';

// Ordine rail FISSO nella shell (design §3): moduli PARI GRADO, Cruscotto in
// testa (→ Action Hub F3). Il Rail (R2) e la MobileBar rendono SEMPRE in questo
// ordine, qualunque sia l'ordine di registrazione.
// 'tel' (Centralino) 7° e 'fatt' (Fatturazione elettronica) 8°.
// NB 'tel': il suo bundle NON sta in questo repo (vive su task/centralino ed è
// stato innestato in produzione a mano, /root/patch-manifest.js). Resta in questa
// lista perché è LIVE E VISIBILE agli utenti: toglierlo lo farebbe sparire dal
// rail al primo deploy di questa shell. In locale il bottone c'è ma il bundle no.
export const RAIL_ORDER = ['dash', 'proc', 'amm', 'cont', 'email', 'sop', 'tel', 'fatt'];

// Moduli con KILL-SWITCH: VISIBILI di default, si spengono per-browser scrivendo
// '0' nella loro chiave localStorage.
//   '0' → nascosto · qualunque altro valore (o assente) → visibile
// (Fino al 2026-07-20 'fatt' era nascosto fuori da locale; decisione utente del
// 21/07 al primo deploy: visibile a tutti. Il kill-switch resta come rete.)
export const RAIL_GATED = { fatt: 'shell_fatt_v1' };

export function railVisible(id, win) {
  const flagKey = RAIL_GATED[id];
  if (!flagKey) return true; // modulo senza kill-switch: sempre visibile
  const w = win || (typeof window !== 'undefined' ? window : null);
  if (!w) return true; // nessuna finestra (test/SSR): visibile
  let v = null;
  try { v = w.localStorage.getItem(flagKey); } catch (e) { v = null; } // storage negato
  return v !== '0';
}

const VALID_ID = /^[a-z][a-z0-9-]*$/;

// campo → tipo atteso ('array' = Array.isArray)
const FIELD_TYPES = {
  label: 'string',
  icon: 'string',
  version: 'number',
  submenu: 'string',
  nav: 'array',
  mount: 'function',
  unmount: 'function',
  navigate: 'function',
  openDeepLink: 'function',
  onShow: 'function',
  onHide: 'function',
};

export function validateSpec(spec) {
  const errors = [];
  if (!spec || typeof spec !== 'object') return ['spec: atteso oggetto {id, ...}'];
  if (typeof spec.id !== 'string' || !VALID_ID.test(spec.id)) {
    errors.push('id: obbligatorio, formato [a-z][a-z0-9-]*');
  }
  for (const f of Object.keys(FIELD_TYPES)) {
    const v = spec[f];
    if (v === undefined || v === null) continue;
    const t = FIELD_TYPES[f];
    const ok = t === 'array' ? Array.isArray(v) : typeof v === t;
    if (!ok) errors.push(f + ': atteso ' + t);
  }
  if (spec.submenu !== undefined && spec.submenu !== null && spec.submenu !== 'shell' && spec.submenu !== 'internal') {
    errors.push("submenu: atteso 'shell' | 'internal'");
  }
  if (spec.version !== undefined && spec.version !== null && spec.version !== 1 && spec.version !== 2) {
    errors.push('version: atteso 1 | 2 (assente = v1 legacy)');
  }
  return errors;
}

export function createRegistry(bus, opts = {}) {
  const isDev = opts.isDev || (() => defaultIsDev(opts.win));
  const logger = opts.logger || console;
  const mods = new Map(); // id -> spec (merged) — insertion order = ordine di registrazione
  const listeners = new Set();

  function notify(kind, entry) {
    listeners.forEach((fn) => {
      try { fn(kind, entry); } catch (e) { logger.error('[registry] listener', e); }
    });
  }

  function register(spec) {
    const errors = validateSpec(spec);
    if (errors.length) {
      throw new Error('[registry] register non valida: ' + errors.join('; '));
    }
    const prev = mods.get(spec.id) || null;
    if (!prev) {
      const entry = Object.assign({ version: 1 }, spec);
      mods.set(spec.id, entry);
      notify('register', entry);
      return entry;
    }
    const prevV = prev.version || 1;
    const newV = spec.version || 1;
    // v1→v1: parità wsh (sovrascrittura/merge); v1→v2: upgrade adapter (R4).
    // v2→v2 o v2→v1: due proprietari per lo stesso id = collisione.
    const isCollision = prevV === 2;
    if (isCollision) {
      const msg = '[registry] collisione id "' + spec.id + '": register v' + newV + ' sopra un modulo già v' + prevV + ' — due proprietari per lo stesso id (B2.9)';
      if (isDev()) throw new Error(msg);
      logger.error(msg);
      return prev; // prod: si preserva il registrato, mai duplicato
    }
    const entry = Object.assign({}, prev, spec); // upgrade: i campi nuovi vincono, quelli non ridichiarati restano
    mods.set(spec.id, entry);
    notify('upgrade', entry);
    return entry;
  }

  // R4: patch di una entry GIÀ registrata (stesso proprietario) — è il canale del
  // fallback automatico dei bridge (§6.2: assertBridge rosso ⇒ submenu:'internal').
  // NON è una register: id e version non si cambiano; entry assente = throw
  // (aggiornare un modulo mai registrato è un bug, non uno stato).
  function update(id, patch) {
    const prev = mods.get(id);
    if (!prev) throw new Error('[registry] update su id non registrato: "' + id + '"');
    const p = Object.assign({}, patch);
    delete p.id;
    delete p.version;
    const errors = validateSpec(Object.assign({ id }, p));
    if (errors.length) throw new Error('[registry] update non valida: ' + errors.join('; '));
    const entry = Object.assign({}, prev, p);
    mods.set(id, entry);
    notify('update', entry);
    return entry;
  }

  function get(id) { return mods.get(id) || null; }
  function has(id) { return mods.has(id); }
  function list() { return Array.from(mods.values()); }

  function onChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  return { register, update, get, has, list, onChange, validateSpec };
}
